import { PomodoroService } from "features/widget-workspace/service/PomodoroService";
import React, { useMemo, useState } from "react";
import styled, { css } from "styled-components";
import { TimerAlarmSoundsMap } from "@lifeat/shared/data-models/widget/Pomodoro/PomodoroWidgetSettingsEntity";
import LifeAtButton from "@common/LifeAtButton/LifeAtButton";
import { AppearanceContextType, useAppearanceContext } from "@contexts/AppearanceContext";
import { GlobalAudioContextType, useGlobalAudioContext } from "@contexts/GlobalAudioContext";
import SettingsIcon from "@icons/SettingsGearIcon.svg";
import { getAlarmSound } from "./PomodoroUtil";
import TimerAudioComponent from "./TimerAudioComponent";
import TimerCountdownAndActionsRow from "./TimerCountdownAndActionsRow";
import TimerSettings from "./TimerSettings";
import { LONG_BREAK_LABEL, POMODORO_LABEL, SHORT_BREAK_LABEL } from "./TimerWidgetConstants";
import { PomodoroSettingsInput, PomodoroSettingsInputAction } from "./hooks/usePomodoroSettingsInput";

const StyledPomodoroTimer = styled.div<{ hidden: boolean }>`
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    padding: 10px 0;

    background: ${({ theme }) => theme.backgroundPrimary};
    color: ${({ theme }) => theme.textColorPrimary};

    ${({ hidden }) =>
        hidden &&
        css`
            display: none;
        `}
`;

const TimerSelections = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    width: auto;
`;

const StyledSettingsIcon = styled(SettingsIcon)`
    fill: ${({ theme }) => theme.iconColor};
`;

const Stroke = styled.span<{ lightMode: boolean }>`
    align-self: center;
    width: 75%;

    ${({ lightMode }) =>
        lightMode
            ? css`
                  border: 1px solid #efa18f;
              `
            : css`
                  border: 1px solid #afb3b7;
              `}
`;

const TimerOption = styled.span`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    .lifeat-button {
        height: unset;
        padding: 6px 7px;
    }
`;

interface PomodoroProps {
    hidden: boolean;
    /**
     * If the timer can have its settings edited.
     */
    canEditSettings: boolean;
    /**
     * Label to display in button when timer is running.
     */
    stopTimerActionButtonLabel: string;
    isRunning: boolean;
    timerDisplayText?: string;
    alarmSoundOptions: TimerAlarmSoundsMap;
    pomodoroService: PomodoroService;
    pomodoroSettingsInput: PomodoroSettingsInput;
    dispatchPomodoroSettingsInput: React.Dispatch<PomodoroSettingsInputAction>;
    timerAudioRef: React.MutableRefObject<HTMLAudioElement | null>;
    /**
     * The volume that the timer audio should play back at.
     * This is a value between 0 and 1.
     */
    timerVolume: number;
    /**
     * Sets the volume that the timer audio should play back at.
     * Accepts value between 0 and 1.
     */
    setTimerVolume: (newVol: number) => void;
    onTimerToggle?: () => void;
    onTimerReset?: () => void;
    onSelectPomodoro?: () => void;
    onSelectShortBreak?: () => void;
    onSelectLongBreak?: () => void;
    onSettingsSave?: () => void;
    isServerActionLoading?: boolean;
    allowAutotransitionToggle: boolean;
}
export default function Pomodoro({
    hidden,
    canEditSettings,
    stopTimerActionButtonLabel,
    isRunning,
    timerDisplayText,
    alarmSoundOptions,
    pomodoroService,
    pomodoroSettingsInput,
    dispatchPomodoroSettingsInput,
    timerAudioRef,
    timerVolume,
    setTimerVolume,
    onTimerToggle,
    onTimerReset,
    onSelectPomodoro,
    onSelectShortBreak,
    onSelectLongBreak,
    onSettingsSave,
    isServerActionLoading,
    allowAutotransitionToggle,
}: PomodoroProps) {
    const { selectedAlarmSound, timerType } = pomodoroSettingsInput;

    const { lightMode }: AppearanceContextType = useAppearanceContext();
    const { masterVolume }: GlobalAudioContextType = useGlobalAudioContext();

    const alarmSound = useMemo(
        () => getAlarmSound(alarmSoundOptions, selectedAlarmSound),
        [alarmSoundOptions, selectedAlarmSound],
    );
    const [showSettings, setShowSettings] = useState(false);

    const stroke = <Stroke lightMode={lightMode} />;

    const toggleSettingsVisible = () => {
        setShowSettings(!showSettings);
    };

    const handleSettingsSave = () => {
        onSettingsSave?.();
        setShowSettings(false);
    };

    return (
        <StyledPomodoroTimer hidden={hidden}>
            <TimerCountdownAndActionsRow
                timerDisplayText={timerDisplayText}
                isRunning={isRunning}
                stopTimerActionButtonLabel={stopTimerActionButtonLabel}
                canEditSettings={canEditSettings}
                onTimerToggle={onTimerToggle}
                onTimerReset={onTimerReset}
                isServerActionLoading={isServerActionLoading}
            />
            <TimerSelections>
                <TimerOption>
                    <LifeAtButton text={POMODORO_LABEL} isLink={false} handleClick={onSelectPomodoro} />
                    {timerType === "pomodoro" || !timerType ? stroke : null}
                </TimerOption>

                <TimerOption>
                    <LifeAtButton text={SHORT_BREAK_LABEL} isLink={false} handleClick={onSelectShortBreak} />
                    {timerType === "short_break" ? stroke : null}
                </TimerOption>

                <TimerOption>
                    <LifeAtButton text={LONG_BREAK_LABEL} isLink={false} handleClick={onSelectLongBreak} />
                    {timerType === "long_break" ? stroke : null}
                </TimerOption>
                <LifeAtButton isLink={false} icon={<StyledSettingsIcon />} handleClick={toggleSettingsVisible} />
            </TimerSelections>
            <TimerAudioComponent
                alarmSound={alarmSound}
                timerVolume={timerVolume * masterVolume}
                timerAudioRef={timerAudioRef}
            />
            {showSettings && (
                <TimerSettings
                    canEditSettings={canEditSettings}
                    timerVolume={timerVolume}
                    setTimerVolume={setTimerVolume}
                    pomodoroSettingsInput={pomodoroSettingsInput}
                    dispatch={dispatchPomodoroSettingsInput}
                    alarmSoundOptions={alarmSoundOptions}
                    allowAutotransitionToggle={allowAutotransitionToggle}
                    pomodoroService={pomodoroService}
                    onSettingsSave={handleSettingsSave}
                />
            )}
        </StyledPomodoroTimer>
    );
}
